import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { RhinoOneBadge } from '../../components/RhinoOneBadge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Upload, 
  Save, 
  RefreshCw, 
  Server, 
  Calendar, 
  Code, 
  Shield, 
  Users, 
  Database,
  Activity,
  CheckCircle2,
  Clock,
  Info,
  Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AboutSettings = () => {
  const [platformInfo, setPlatformInfo] = useState({
    version: '1.0.0',
    codename: 'RHINO-1',
    build_date: new Date().toISOString().split('T')[0],
    badge_url: '/assets/branding/rhino-1-badge.png',
    description: 'Enterprise job management and operations platform for Rhino Shield Florida.',
    release_notes: 'Initial release with core job management, communications hub, and crew management features.'
  });
  const [versionHistory, setVersionHistory] = useState([]);
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBadge, setUploadingBadge] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPlatformInfo();
    fetchSystemStats();
  }, []);

  const fetchPlatformInfo = async () => {
    try {
      const response = await axios.get(`${API}/platform-info`);
      if (response.data) {
        setPlatformInfo(response.data);
        if (response.data.version_history) {
          setVersionHistory(response.data.version_history);
        }
      }
    } catch (error) {
      console.log('Using default platform info');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStats = async () => {
    try {
      const [usersRes, jobsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/jobs`),
        axios.get(`${API}/contacts`)
      ]);
      
      setSystemStats({
        total_users: usersRes.data?.length || 0,
        total_jobs: jobsRes.data?.length || 0,
        total_contacts: contactsRes.data?.length || 0,
        last_updated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to fetch system stats:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/platform-info`, platformInfo);
      toast.success('Platform information saved successfully');
    } catch (error) {
      console.error('Failed to save platform info:', error);
      toast.error('Failed to save platform information');
    } finally {
      setSaving(false);
    }
  };

  const handleBadgeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploadingBadge(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'platform_badge');

      const response = await axios.post(`${API}/upload/platform-badge`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.url) {
        setPlatformInfo({ ...platformInfo, badge_url: response.data.url });
        toast.success('Badge uploaded successfully');
      }
    } catch (error) {
      console.error('Failed to upload badge:', error);
      toast.error('Failed to upload badge');
    } finally {
      setUploadingBadge(false);
    }
  };

  const addVersionEntry = () => {
    const newVersion = {
      version: platformInfo.version,
      date: new Date().toISOString().split('T')[0],
      notes: platformInfo.release_notes || 'Version update'
    };
    setVersionHistory([newVersion, ...versionHistory]);
    toast.success('Version entry added to history');
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="about-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">About Platform</h1>
            <p className="text-muted-foreground mt-1">Platform information, versioning, and branding</p>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>

        {/* Platform Identity Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Platform Identity
            </CardTitle>
            <CardDescription>Core platform branding and identification</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start gap-8">
              {/* Badge Preview */}
              <div className="flex flex-col items-center gap-3 p-4 bg-muted/30 rounded-lg border-2 border-dashed border-muted-foreground/20">
                <RhinoOneBadge size="xl" showTooltip={false} />
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingBadge}
                >
                  {uploadingBadge ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload New Badge
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBadgeUpload}
                  className="hidden"
                />
                <p className="text-xs text-muted-foreground text-center">PNG recommended, max 2MB</p>
              </div>

              {/* Platform Info Form */}
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="codename">Platform Codename</Label>
                    <Input
                      id="codename"
                      value={platformInfo.codename}
                      onChange={(e) => setPlatformInfo({ ...platformInfo, codename: e.target.value })}
                      placeholder="RHINO-1"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="version">Version Number</Label>
                    <Input
                      id="version"
                      value={platformInfo.version}
                      onChange={(e) => setPlatformInfo({ ...platformInfo, version: e.target.value })}
                      placeholder="1.0.0"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="build_date">Build Date</Label>
                  <Input
                    id="build_date"
                    type="date"
                    value={platformInfo.build_date}
                    onChange={(e) => setPlatformInfo({ ...platformInfo, build_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Platform Description</Label>
                  <Textarea
                    id="description"
                    value={platformInfo.description}
                    onChange={(e) => setPlatformInfo({ ...platformInfo, description: e.target.value })}
                    placeholder="Describe the platform..."
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Release Notes Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5 text-primary" />
              Release Notes
            </CardTitle>
            <CardDescription>Current version release notes and changelog</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="release_notes">Current Release Notes</Label>
              <Textarea
                id="release_notes"
                value={platformInfo.release_notes}
                onChange={(e) => setPlatformInfo({ ...platformInfo, release_notes: e.target.value })}
                placeholder="Document the changes in this version..."
                rows={4}
              />
            </div>
            <Button variant="outline" onClick={addVersionEntry}>
              <Clock className="h-4 w-4 mr-2" />
              Add to Version History
            </Button>
          </CardContent>
        </Card>

        {/* Version History */}
        {versionHistory.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Version History
              </CardTitle>
              <CardDescription>Previous platform versions and release notes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {versionHistory.map((entry, index) => (
                  <div key={index} className="flex items-start gap-4 p-3 bg-muted/30 rounded-lg">
                    <Badge variant="outline" className="mt-1">v{entry.version}</Badge>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <Calendar className="h-3 w-3" />
                        {entry.date}
                      </div>
                      <p className="text-sm">{entry.notes}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* System Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              System Statistics
            </CardTitle>
            <CardDescription>Current platform usage and health metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-2">
                  <Users className="h-4 w-4" />
                  <span className="text-sm font-medium">Users</span>
                </div>
                <div className="text-2xl font-bold">{systemStats?.total_users || '—'}</div>
              </div>
              
              <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                  <Zap className="h-4 w-4" />
                  <span className="text-sm font-medium">Jobs</span>
                </div>
                <div className="text-2xl font-bold">{systemStats?.total_jobs || '—'}</div>
              </div>
              
              <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-2">
                  <Database className="h-4 w-4" />
                  <span className="text-sm font-medium">Contacts</span>
                </div>
                <div className="text-2xl font-bold">{systemStats?.total_contacts || '—'}</div>
              </div>
              
              <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">Status</span>
                </div>
                <div className="text-lg font-bold text-green-600">Operational</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5 text-primary" />
              Technical Information
            </CardTitle>
            <CardDescription>Platform technical details and environment info</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Frontend Framework</span>
                  <span className="font-medium">React 18</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Backend Framework</span>
                  <span className="font-medium">FastAPI (Python)</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Database</span>
                  <span className="font-medium">MongoDB</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">UI Components</span>
                  <span className="font-medium">Shadcn/UI + Tailwind</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Hosting</span>
                  <span className="font-medium">Kubernetes</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Environment</span>
                  <span className="font-medium">Production</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Powered By Footer */}
        <div className="flex items-center justify-center gap-3 py-6 text-sm text-muted-foreground">
          <RhinoOneBadge size="sm" />
          <span>Powered by <strong className="text-foreground">{platformInfo.codename}</strong> Platform</span>
          <span className="text-muted-foreground/50">•</span>
          <span>v{platformInfo.version}</span>
        </div>
      </div>
    </Layout>
  );
};

export default AboutSettings;
